import { Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { BusinessPartnerSelector } from '../../common/components/business-partner-selector/business-partner-selector';
import { BusinessPartner } from '../../model/business_partner';
import {
  BusinessPartnerProviderConfig,
  Provider,
  UpsertBusinessPartnerProviderPayload
} from '../../model/provider';
import { CopilotApiService } from '../../services/copilot-api-service';
import { ToastService } from '../../services/toast.service';
import { BusinessPartnersService } from '../../common/services/business-partners.service';
import { ProviderCard } from './card/provider-card';
import { ProviderDialog } from './dialog/provider-dialog';
import { DeleteProviderDialog } from './dialog/delete-provider-dialog';

@Component({
  selector: 'app-providers',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    BusinessPartnerSelector,
    ProviderCard,
    ProviderDialog,
    DeleteProviderDialog
  ],
  templateUrl: './providers.html',
  styleUrl: './providers.css'
})
export class Providers implements OnInit, OnDestroy {
  protected readonly providers = signal<BusinessPartnerProviderConfig[] | null>(null);
  protected readonly loading = signal<boolean>(false);
  protected readonly error = signal<string | null>(null);
  protected readonly showAddDialog = signal<boolean>(false);
  protected readonly editingProvider = signal<BusinessPartnerProviderConfig | null>(null);
  protected readonly deletingProvider = signal<BusinessPartnerProviderConfig | null>(null);
  protected readonly submitting = signal<boolean>(false);
  protected readonly providerTypes = signal<Provider[]>([]);
  protected readonly providerTypesLoading = signal<boolean>(false);
  protected readonly selectedBusinessPartnerId = signal<number | null>(null);
  protected readonly selectedBusinessPartnerName = signal<string>('FleetBooster');
  protected readonly hasSelectedBusinessPartner = computed(() => this.selectedBusinessPartnerId() != null);
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly api: CopilotApiService,
    private readonly toast: ToastService,
    private readonly translate: TranslateService,
    private readonly businessPartnersService: BusinessPartnersService
  ) {}

  ngOnInit(): void {
    this.businessPartnersService.initialize();
    this.subscriptions.add(this.businessPartnersService.providers$.subscribe((providers) => {
      this.providerTypes.set((providers || []).slice());
    }));
    this.subscriptions.add(this.businessPartnersService.providersLoading$.subscribe((loading) => {
      this.providerTypesLoading.set(loading);
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected onBusinessPartnerChange(partner: BusinessPartner | null): void {
    this.selectedBusinessPartnerId.set(partner?.Id ?? null);
    this.selectedBusinessPartnerName.set(partner?.CompanyName || partner?.Name || 'FleetBooster');
    this.closeAddDialog();
    this.closeEditDialog();
    this.closeDeleteDialog();

    if (partner?.Id == null) {
      this.providers.set(null);
      this.loading.set(false);
      this.error.set(null);
      return;
    }

    this.loadProvidersForBusinessPartner(partner.Id);
  }

  protected openAddDialog(): void {
    if (!this.hasSelectedBusinessPartner()) {
      return;
    }
    this.showAddDialog.set(true);
  }

  protected closeAddDialog(): void {
    this.showAddDialog.set(false);
    this.submitting.set(false);
  }

  protected openEditDialog(provider: BusinessPartnerProviderConfig): void {
    this.editingProvider.set(provider);
  }

  protected closeEditDialog(): void {
    this.editingProvider.set(null);
    this.submitting.set(false);
  }

  protected openDeleteDialog(provider: BusinessPartnerProviderConfig): void {
    this.deletingProvider.set(provider);
  }

  protected closeDeleteDialog(): void {
    this.deletingProvider.set(null);
    this.submitting.set(false);
  }

  protected onSubmitAdd(payload: UpsertBusinessPartnerProviderPayload): void {
    const businessPartnerId = this.selectedBusinessPartnerId();
    if (businessPartnerId == null) {
      return;
    }

    this.submitting.set(true);
    this.api.addBusinessPartnerProvider({
      ...payload,
      BusinessPartnerId: businessPartnerId
    }).subscribe({
      next: () => {
        this.toast.success(this.translate.instant('providers.create_success_toast'), 3000);
        this.submitting.set(false);
        this.closeAddDialog();
        this.businessPartnersService.refreshPartnerProviderLinks();
        this.loadProvidersForBusinessPartner(businessPartnerId);
      },
      error: (err) => {
        this.submitting.set(false);
        this.toast.error(this.readErrorMessage(err, 'providers.create_error_toast'), 3000);
      }
    });
  }

  protected onSubmitEdit(payload: UpsertBusinessPartnerProviderPayload): void {
    const currentProvider = this.editingProvider();
    const businessPartnerId = this.selectedBusinessPartnerId();
    if (currentProvider == null || businessPartnerId == null) {
      return;
    }

    this.submitting.set(true);
    this.api.updateBusinessPartnerProvider({
      ...payload,
      Id: currentProvider.Id
    }).subscribe({
      next: () => {
        this.toast.success(this.translate.instant('providers.update_success_toast'), 3000);
        this.submitting.set(false);
        this.closeEditDialog();
        this.businessPartnersService.refreshPartnerProviderLinks();
        this.loadProvidersForBusinessPartner(businessPartnerId);
      },
      error: (err) => {
        this.submitting.set(false);
        this.toast.error(this.readErrorMessage(err, 'providers.update_error_toast'), 3000);
      }
    });
  }

  protected onConfirmDelete(): void {
    const provider = this.deletingProvider();
    const businessPartnerId = this.selectedBusinessPartnerId();
    if (provider == null || businessPartnerId == null) {
      return;
    }

    this.submitting.set(true);
    this.api.deleteBusinessPartnerProvider({ Id: provider.Id }).subscribe({
      next: () => {
        this.toast.success(this.translate.instant('providers.delete_success_toast'), 3000);
        this.submitting.set(false);
        this.closeDeleteDialog();
        this.businessPartnersService.refreshPartnerProviderLinks();
        this.loadProvidersForBusinessPartner(businessPartnerId);
      },
      error: (err) => {
        this.submitting.set(false);
        this.toast.error(this.readErrorMessage(err, 'providers.delete_error_toast'), 3000);
      }
    });
  }

  private loadProvidersForBusinessPartner(businessPartnerId: number): void {
    this.loading.set(true);
    this.error.set(null);
    this.providers.set(null);

    this.api.getBusinessPartnerProviders(businessPartnerId).subscribe({
      next: (providers) => {
        this.providers.set((providers || []).slice());
        this.loading.set(false);
      },
      error: () => {
        this.providers.set([]);
        this.loading.set(false);
        this.error.set(this.translate.instant('providers.loading_error'));
      }
    });
  }

  private readErrorMessage(error: unknown, fallbackKey: string): string {
    if (!error || typeof error !== 'object') {
      return this.translate.instant(fallbackKey);
    }

    const responseError = (error as { error?: unknown }).error;
    if (responseError && typeof responseError === 'object' && !Array.isArray(responseError)) {
      const errorMessage = (responseError as { error?: unknown }).error;
      if (typeof errorMessage === 'string' && errorMessage.trim().length > 0) {
        return errorMessage;
      }
    }

    return this.translate.instant(fallbackKey);
  }
}
